import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { IoMdArrowBack } from "react-icons/io";
import { FiUploadCloud, FiX, FiImage, FiCheck } from "react-icons/fi";
import { FaGift, FaWandMagicSparkles, FaXmark } from "react-icons/fa6";
import {
  getCategoryListingPaginate,
  getManufacturesListingPaginate,
  getSubCategoryListingPaginate,
} from "../../api/context/manufacturer";
import { getBrandListing } from "../../api/context/brand";
import { createAiProduct } from "../../api/context/products";
import { SelectionContext } from "../../Context/Store";
import PaywallModal from "../Billing/components/PaywallModal";
import InvalidImageModal from "./InvalidImageModal";

import goodLit from "../../assets/images/guidelines/good-lit.webp";
import goodBg from "../../assets/images/guidelines/good-bg.webp";
import goodFrame from "../../assets/images/guidelines/good-frame.webp";
import goodAngle from "../../assets/images/guidelines/good-angle.webp";
import badLit from "../../assets/images/guidelines/bad-lit.webp";
import badBg from "../../assets/images/guidelines/bad-bg.webp";
import badFrame from "../../assets/images/guidelines/bad-frame.webp";
import badBlur from "../../assets/images/guidelines/bad-blur.webp";

const GUIDELINE_IMAGES = {
  "good-lit": goodLit,
  "good-bg": goodBg,
  "good-frame": goodFrame,
  "good-angle": goodAngle,
  "bad-lit": badLit,
  "bad-bg": badBg,
  "bad-frame": badFrame,
  "bad-blur": badBlur,
};

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png"];

const LOADING_STEPS = [
  { text: "Uploading photos...", duration: 6000 },
  { text: "Analyzing the image...", duration: 7000 },
  { text: "Extracting product details...", duration: 7000 },
  { text: "Detecting product color and style...", duration: 7000 },
  { text: "Writing product title...", duration: 4000 },
  { text: "Writing product description...", duration: 6000 },
  { text: "Polishing the final draft...", duration: 7000 },
  { text: "Almost done...", duration: null },
];

const Dropzone = ({ label, preview, error, onFile, onRemove }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div className="ai-dropzone-wrap">
      <div className="ai-dropzone-label">
        <FiImage /> {label}
      </div>
      <div
        className={[
          "ai-dropzone",
          dragging ? "dragging" : "",
          preview ? "has-image" : "",
          error ? "has-error" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => !preview && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {preview ? (
          <>
            <img src={preview} alt={label} className="ai-dropzone-preview" />
            <button
              type="button"
              className="ai-dropzone-remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={`Remove ${label}`}
            >
              <FiX />
            </button>
          </>
        ) : (
          <div className="ai-dropzone-empty">
            <FiUploadCloud className="ai-upload-icon" />
            <div className="ai-dropzone-primary">
              Drop image or <span className="link">browse</span>
            </div>
            <div className="ai-dropzone-secondary">JPG · PNG · up to 10 MB</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
        />
      </div>
      {error && <div className="ai-error">{error}</div>}
    </div>
  );
};

const GuidelineItem = ({ variant, label }) => (
  <div className="ai-guideline-item">
    <div className="ai-guideline-thumb">
      <img src={GUIDELINE_IMAGES[variant]} alt={label} />
    </div>
    <div className="ai-guideline-label">{label}</div>
  </div>
);

const CreateAiInventory = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const isVendor = user?.userType === "Vendor";

  const vendorSelfOption = isVendor
    ? {
        value: user?.vendorId,
        label:
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
          user?.storeName ||
          user?.email ||
          "Your Store",
      }
    : null;

  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [imageErrors, setImageErrors] = useState({ front: "", back: "" });

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  const { billingBalance, refreshBillingBalance, hasAttemptedAi, markAttemptedAi } = useContext(SelectionContext);

  useEffect(() => {
    if (isVendor) refreshBillingBalance();
  }, [isVendor, refreshBillingBalance]);

  const totalAvailable = billingBalance?.totalAvailable ?? null;
  const trialAvailableOnBalance = billingBalance?.trial?.available === true;
  const trialGrantAmount = billingBalance?.trial?.grantAmount ?? 3;
  const hasClaimableTrial = isVendor && trialAvailableOnBalance;
  const hasBalance = totalAvailable != null && totalAvailable > 0;
  const isTrialOnly = hasClaimableTrial && !hasBalance;
  const isOutOfCredits =
    isVendor && hasAttemptedAi && totalAvailable === 0 && !hasClaimableTrial;
  const [paywall, setPaywall] = useState(null);
  const [invalidImage, setInvalidImage] = useState(null);

  useEffect(() => {
    return () => {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!submitting) {
      setLoadingStep(0);
      return;
    }
    const timeouts = [];
    let cumulative = 0;
    for (let i = 1; i < LOADING_STEPS.length; i++) {
      cumulative += LOADING_STEPS[i - 1].duration ?? 0;
      timeouts.push(setTimeout(() => setLoadingStep(i), cumulative));
    }
    return () => timeouts.forEach(clearTimeout);
  }, [submitting]);

  const schema = yup.object().shape({
    categoryId: yup
      .number()
      .typeError("Category is required")
      .required("Category is required"),
    subCategoryId: yup
      .number()
      .typeError("Sub-category is required")
      .required("Sub-category is required"),
    manufacturerId: isVendor
      ? yup.mixed()
      : yup
          .number()
          .typeError("Vendor is required")
          .required("Vendor is required"),
  });

  const formik = useFormik({
    initialValues: {
      categoryId: "",
      subCategoryId: "",
      manufacturerId: isVendor ? user?.vendorId ?? "" : "",
      brandId: "",
      selectedCategory: null,
      selectedSubCategory: null,
      selectedManufacturer: vendorSelfOption,
      selectedBrand: null,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!frontFile) {
        setImageErrors((p) => ({ ...p, front: "Front image is required" }));
        return;
      }
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("frontImage", frontFile);
        if (backFile) fd.append("backImage", backFile);
        fd.append("categoryId", values.categoryId);
        fd.append("subCategoryId", values.subCategoryId);
        fd.append("subSubCategoryId", values.subCategoryId);
        if (values.selectedCategory?.label)
          fd.append("categoryName", values.selectedCategory.label);
        if (values.selectedSubCategory?.label)
          fd.append("subCategoryName", values.selectedSubCategory.label);
        const mfrId = isVendor ? user?.vendorId : values.manufacturerId;
        if (mfrId) fd.append("manufacturerId", mfrId);
        if (values.brandId) fd.append("brandId", values.brandId);

        const response = await createAiProduct(fd);
        markAttemptedAi();
        const productId = response?.data?.data?.product?.id;
        if (response?.data?.success && productId) {
          toast.success("AI product created successfully");
          refreshBillingBalance();
          navigate(`/edit-inventory/${productId}`);
        } else {
          toast.error(response?.data?.message || "Failed to create AI product");
        }
      } catch (err) {
        markAttemptedAi();
        const code = err?.response?.data?.code;
        if (
          err?.response?.status === 402 &&
          (code === "INSUFFICIENT_CREDITS" || code === "TRIAL_AVAILABLE")
        ) {
          setPaywall({
            balance: err.response.data.balance,
            message: err.response.data.message,
            trialAvailable: code === "TRIAL_AVAILABLE",
          });
          refreshBillingBalance();
        } else if (err?.code === "ECONNABORTED") {
          toast.error(
            "The AI took too long to respond. Please try again with clearer photos."
          );
        } else if (
          err?.response?.status === 400 &&
          code === "INVALID_PRODUCT_IMAGE"
        ) {
          setInvalidImage({
            rejectionReason: err.response.data.rejectionReason,
            message: err.response.data.message,
          });
        } else if (err?.response?.status === 400) {
          toast.error(
            err.response?.data?.message ||
              "Please check your inputs and try again"
          );
        } else {
          toast.error(
            err?.response?.data?.message ||
              "Something went wrong generating this listing. Please try again with clearer photos."
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getCategoryListingPaginate({ page: 1, limit: 10, search: "" })((res) => {
      if (res?.statusCode === 200) setCategoryOptions(res?.results || []);
    });
    if (!isVendor) {
      getManufacturesListingPaginate({ page: 1, limit: 10, search: "" })(
        (res) => {
          if (res?.statusCode === 200)
            setManufacturerOptions(res?.results || []);
        }
      );
    }
  }, [isVendor]);

  useEffect(() => {
    if (!formik.values.categoryId) {
      setSubCategoryOptions([]);
      return;
    }
    getSubCategoryListingPaginate({
      page: 1,
      limit: 100,
      search: "",
      categoryIds: [formik.values.categoryId],
    })((res) => {
      if (res?.statusCode === 200) setSubCategoryOptions(res?.results || []);
      else setSubCategoryOptions([]);
    });
  }, [formik.values.categoryId]);

  useEffect(() => {
    const vendorId = isVendor
      ? user?.vendorId
      : formik.values.manufacturerId || null;
    if (!vendorId) {
      setBrandOptions([]);
      return;
    }
    getBrandListing({ vendorId, page: 1, pageSize: 10, search: "" })((res) => {
      if (res?.statusCode === 200) {
        const opts = (res?.data || []).map((b) => ({
          value: b.id,
          label: b.name,
        }));
        setBrandOptions(opts);
        if (opts.length === 1) {
          formik.setFieldValue("brandId", opts[0].value);
          formik.setFieldValue("selectedBrand", opts[0]);
        }
      } else setBrandOptions([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.manufacturerId, isVendor, user?.vendorId]);

  let typingTimer;
  const debounce = (fn, ms = 300) => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(fn, ms);
  };

  const loadCategoryOptions = (input) =>
    new Promise((resolve) => {
      debounce(() => {
        getCategoryListingPaginate({ page: 1, limit: 10, search: input })(
          (res) => {
            if (res?.statusCode === 200) {
              setCategoryOptions(res?.results || []);
              resolve(res?.results || []);
            } else resolve([]);
          }
        );
      });
    });

  const loadManufacturerOptions = (input) =>
    new Promise((resolve) => {
      debounce(() => {
        getManufacturesListingPaginate({ page: 1, limit: 10, search: input })(
          (res) => {
            if (res?.statusCode === 200) {
              setManufacturerOptions(res?.results || []);
              resolve(res?.results || []);
            } else resolve([]);
          }
        );
      });
    });

  const loadBrandOptions = (input) =>
    new Promise((resolve) => {
      const vendorId = isVendor
        ? user?.vendorId
        : formik.values.manufacturerId;
      if (!vendorId) return resolve([]);
      debounce(() => {
        getBrandListing({
          vendorId,
          page: 1,
          pageSize: 10,
          search: input,
        })((res) => {
          if (res?.statusCode === 200) {
            const opts = (res?.data || []).map((b) => ({
              value: b.id,
              label: b.name,
            }));
            setBrandOptions(opts);
            resolve(opts);
          } else resolve([]);
        });
      });
    });

  const validateImage = (file) => {
    if (!ACCEPTED_MIME.includes(file.type))
      return "Only JPG, JPEG, or PNG allowed";
    if (file.size > MAX_IMAGE_BYTES) return "Max file size is 10 MB";
    return "";
  };

  const setImage = (side, file) => {
    const err = validateImage(file);
    if (err) {
      setImageErrors((p) => ({ ...p, [side]: err }));
      return;
    }
    setImageErrors((p) => ({ ...p, [side]: "" }));
    const preview = URL.createObjectURL(file);
    if (side === "front") {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(file);
      setFrontPreview(preview);
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(file);
      setBackPreview(preview);
    }
  };

  const removeImage = (side) => {
    if (side === "front") {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(null);
      setFrontPreview("");
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(null);
      setBackPreview("");
    }
  };

  const isReady =
    frontFile &&
    formik.values.categoryId &&
    formik.values.subCategoryId &&
    (isVendor || formik.values.manufacturerId);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      padding: "1px 2px",
      fontSize: "13px",
      minHeight: "38px",
      borderColor: state.isFocused ? "#008695" : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(0, 134, 149, 0.15)"
        : "none",
      "&:hover": { borderColor: "#008695" },
    }),
    menu: (base) => ({ ...base, fontSize: "13px", zIndex: 20 }),
    placeholder: (base) => ({ ...base, fontSize: "13px" }),
    singleValue: (base) => ({ ...base, fontSize: "13px" }),
  };

  return (
    <div>
      <div className="page-header-sticky d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-start">
              <div
                onClick={() => !submitting && navigate(-1)}
                style={{
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: "22px",
                  fontWeight: "800",
                  display: "flex",
                  alignItems: "center",
                  opacity: submitting ? 0.5 : 1,
                  lineHeight: 1,
                  marginTop: "4px",
                  marginRight: "10px",
                }}
              >
                <IoMdArrowBack className="icon" />
              </div>
              <div>
                <h3 className="mb-1 fw-bold d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '20px' }}>
                  AI Product Listing
                  <span className="ai-pill">
                    <FaWandMagicSparkles /> Powered by Getto AI
                  </span>
                </h3>
                <p className="text-muted mb-0">
                  Upload photos and let AI draft your listing.
                </p>
              </div>
            </div>

            <div className="ai-header-cta">
              {isTrialOnly ? (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn ai-generate-btn-trial"
                    onClick={() => navigate("/billing")}
                  >
                    <FaGift className="ai-icon" />
                    Claim {trialGrantAmount} free credits
                  </Button>
                  <div className="ai-cost-note is-trial">
                    On the house · no card required
                  </div>
                </>
              ) : isOutOfCredits ? (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn ai-generate-btn-empty"
                    onClick={() => navigate("/billing")}
                  >
                    <FaWandMagicSparkles className="ai-icon" />
                    Get credits
                  </Button>
                  <div className="ai-cost-note is-empty">
                    0 credits · Buy a plan or top-up
                  </div>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn"
                    disabled={!isReady || submitting}
                    onClick={() => formik.handleSubmit()}
                  >
                    <FaWandMagicSparkles className="ai-icon" />
                    Generate Product
                  </Button>
                  <div className="ai-cost-note">Uses 1 credit</div>
                  {hasClaimableTrial && (
                    <button
                      type="button"
                      className="ai-trial-hint"
                      onClick={() => navigate("/billing")}
                    >
                      <FaGift />
                      {trialGrantAmount} free credits waiting · Claim
                    </button>
                  )}
                </>
              )}
            </div>
      </div>

      <Form onSubmit={formik.handleSubmit} noValidate>
        <Row>
          <Col md={7}>
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light">
                <span style={{ fontSize: "15px", fontWeight: "600" }}>
                  Product Photos
                </span>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <Dropzone
                      label="Front View"
                      preview={frontPreview}
                      error={imageErrors.front}
                      onFile={(f) => setImage("front", f)}
                      onRemove={() => removeImage("front")}
                    />
                  </Col>
                  <Col md={6}>
                    <Dropzone
                      label="Back View (optional)"
                      preview={backPreview}
                      error={imageErrors.back}
                      onFile={(f) => setImage("back", f)}
                      onRemove={() => removeImage("back")}
                    />
                  </Col>
                </Row>

                <div className="ai-guidelines">
                  <div className="ai-guidelines-heading">
                    <FiImage /> Photo guidelines for the best AI results
                  </div>

                  <div className="ai-guideline-row ai-guideline-row-do">
                    <div className="ai-guideline-row-title">
                      <FiCheck /> Do
                    </div>
                    <div className="ai-guideline-row-items">
                      <GuidelineItem variant="good-lit" label="Well-lit" />
                      <GuidelineItem variant="good-bg" label="Neutral background" />
                      <GuidelineItem variant="good-frame" label="Fills the frame" />
                      <GuidelineItem variant="good-angle" label="Straight-on angle" />
                    </div>
                  </div>

                  <div className="ai-guideline-row ai-guideline-row-dont">
                    <div className="ai-guideline-row-title">
                      <FaXmark /> Avoid
                    </div>
                    <div className="ai-guideline-row-items">
                      <GuidelineItem variant="bad-lit" label="Dim or dark" />
                      <GuidelineItem variant="bad-bg" label="Busy background" />
                      <GuidelineItem variant="bad-frame" label="Product too small" />
                      <GuidelineItem variant="bad-blur" label="Blurry or filtered" />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={5}>
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light">
                <span style={{ fontSize: "15px", fontWeight: "600" }}>
                  Category
                </span>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "13px" }}
                  >
                    Category *
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={categoryOptions}
                    loadOptions={loadCategoryOptions}
                    value={formik.values.selectedCategory}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        categoryId: opt?.value || "",
                        selectedCategory: opt,
                        subCategoryId: "",
                        selectedSubCategory: null,
                      });
                      setSubCategoryOptions([]);
                    }}
                    onBlur={() => formik.setFieldTouched("categoryId", true)}
                    placeholder="Choose category"
                    isClearable
                    styles={selectStyles}
                  />
                  {formik.touched.categoryId && formik.errors.categoryId && (
                    <div className="ai-error">{formik.errors.categoryId}</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "13px" }}
                  >
                    Sub-Category *
                  </Form.Label>
                  <Select
                    key={formik.values.categoryId || "no-cat"}
                    options={subCategoryOptions}
                    value={formik.values.selectedSubCategory}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        subCategoryId: opt?.value || "",
                        selectedSubCategory: opt,
                      });
                    }}
                    onBlur={() => formik.setFieldTouched("subCategoryId", true)}
                    isDisabled={!formik.values.categoryId}
                    placeholder={
                      formik.values.categoryId
                        ? "Choose sub-category"
                        : "Select category first"
                    }
                    isClearable
                    styles={selectStyles}
                  />
                  {formik.touched.subCategoryId &&
                    formik.errors.subCategoryId && (
                      <div className="ai-error">
                        {formik.errors.subCategoryId}
                      </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "13px" }}
                  >
                    Vendor {!isVendor && "*"}
                  </Form.Label>
                  {isVendor ? (
                    <AsyncSelect
                      value={vendorSelfOption}
                      isDisabled
                      styles={selectStyles}
                    />
                  ) : (
                    <>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={manufacturerOptions}
                        loadOptions={loadManufacturerOptions}
                        value={formik.values.selectedManufacturer}
                        onChange={(opt) => {
                          formik.setValues({
                            ...formik.values,
                            manufacturerId: opt?.value || "",
                            selectedManufacturer: opt,
                            brandId: "",
                            selectedBrand: null,
                          });
                        }}
                        onBlur={() =>
                          formik.setFieldTouched("manufacturerId", true)
                        }
                        placeholder="Choose vendor"
                        isClearable
                        styles={selectStyles}
                      />
                      {formik.touched.manufacturerId &&
                        formik.errors.manufacturerId && (
                          <div className="ai-error">
                            {formik.errors.manufacturerId}
                          </div>
                        )}
                    </>
                  )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "13px" }}
                  >
                    Brand
                  </Form.Label>
                  <AsyncSelect
                    key={`brand-${
                      isVendor ? user?.vendorId : formik.values.manufacturerId
                    }`}
                    cacheOptions
                    defaultOptions={brandOptions}
                    loadOptions={loadBrandOptions}
                    value={formik.values.selectedBrand}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        brandId: opt?.value || "",
                        selectedBrand: opt,
                      });
                    }}
                    isDisabled={!isVendor && !formik.values.manufacturerId}
                    placeholder={
                      !isVendor && !formik.values.manufacturerId
                        ? "Select vendor first"
                        : "Choose brand"
                    }
                    isClearable
                    styles={selectStyles}
                  />
                </Form.Group>

                <div
                  className="text-muted mt-4"
                  style={{ fontSize: "12px", lineHeight: 1.5 }}
                >
                  You'll add pricing, stock, and other details on the next step
                  once AI drafts the listing.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Form>

      {submitting && (
        <div className="ai-loading-overlay" role="dialog" aria-live="polite">
          <div className="ai-loading-card">
            <div className="ai-loading-orb">
              <FaWandMagicSparkles />
            </div>
            <div className="ai-loading-status">
              {LOADING_STEPS[loadingStep].text}
            </div>
            <div className="ai-loading-bar">
              <span />
            </div>
            <div className="ai-loading-note">
              This can take up to a minute. Please keep this tab open.
            </div>
          </div>
        </div>
      )}

      <PaywallModal
        isOpen={!!paywall}
        toggle={() => setPaywall(null)}
        balance={paywall?.balance}
        message={paywall?.message}
        trialAvailable={paywall?.trialAvailable}
      />

      <InvalidImageModal
        isOpen={!!invalidImage}
        toggle={() => setInvalidImage(null)}
        rejectionReason={invalidImage?.rejectionReason}
        message={invalidImage?.message}
      />
    </div>
  );
};

export default CreateAiInventory;
